import { Link, useParams, useNavigate, useLocation } from "react-router-dom"
import { useState, useMemo, useCallback, useRef, useEffect } from "react"
import useSWR from "swr"
import { useUploadDocument } from "@/contexts/UploadDocumentContext"
import { toast } from "sonner"
import {
  ArrowLeft,
  Flag,
  Clock,
  DollarSign,
  ArrowUpRight,
  Share2,
  MoreVertical,
  Send,
  Calendar,
  Globe,
  TrendingUp,
  FolderOpen,
  ScrollText,
  Trash2,
} from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { Avatar } from "@/components/ui/Avatar"
import { Menu, MenuTrigger, MenuPopup, MenuItem } from "@/components/ui/menu"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { notifyFeature } from "@/utils/demoNotifications"
import { DealStage } from "@equaliq/api-model-v2"
import { useDealOperations } from "@/hooks/deals/useDealOperations"
import { useSelectedOrgId } from "@/hooks/common/useSelectedOrgId"
import LoadingBar from "@/components/Reusable/LoadingBar"
import { cn } from "src/utils/Functions"
import { motion, AnimatePresence } from "motion/react"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import { getDealApi } from "src/api/deal"
import { getContractApi } from "src/api/contract/GetContractApi"
import type { DealVersion } from "@equaliq/api-model"
import type { DemoParticipant } from "@/types/demo.types"
import type { DemoDeal } from "@/mockData/demoData"
import { PATH_ROOT } from "src/routes/paths"
import { InviteParticipantDialog } from "./components/InviteParticipantDialog"
import {
  WidgetCard,
  DocumentsTable,
  ActionItem,
  ParticipantItem,
  EmptyContractsState,
  KeyTermChart,
  DealProgressTimeline,
} from "./components"
import { useDealPageData } from "@/hooks/deals/useDealPageData"
import { useDealIntermediateData } from "@/hooks/deals/useDealIntermediateData"
import type { InviteParticipantFormData } from "@/types/deal.types"

/** Draggable divider between main content and sidebar */
const DragHandle = () => {
  const handleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handle = handleRef.current
    if (!handle) return

    let startX = 0
    let startWidth = 0

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      const delta = startX - e.clientX
      const newWidth = Math.min(520, Math.max(280, startWidth + delta))
      document.documentElement.style.setProperty("--deal-sidebar-width", `${newWidth}px`)
    }

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = "auto"
    }

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      startX = e.clientX
      const sidebar = handle.nextElementSibling as HTMLElement | null
      startWidth = sidebar?.offsetWidth ?? 380
      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    }

    handle.addEventListener("mousedown", onMouseDown)
    return () => {
      handle.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }
  }, [])

  return (
    <div
      ref={handleRef}
      className="hidden lg:flex w-1 cursor-col-resize items-center justify-center hover:bg-primary/20 active:bg-primary/30 transition-colors group shrink-0"
    >
      <div className="w-px h-8 bg-border group-hover:bg-primary/50 transition-colors rounded-full" />
    </div>
  )
}

export interface DealPageProps {
  dealId?: string
  tab?: string
}

const DealPage: React.FC<DealPageProps> = ({ dealId: propDealId }) => {
  const { id: paramId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // Route is authoritative for which surface we're on — NOT the global
  // useIsDemo() localStorage flag, which is set by the welcome-page toggle and
  // PERSISTS after a demo tour. A user who toured the demo then created a REAL
  // deal on /new-deal (already route-gated) lands here on /deal/<realId>; the
  // stale flag would otherwise make this page skip the real SWR fetch and miss
  // the demo lookup → "Deal Not Found". The demo page lives at /demo/deal/:id
  // (wrapped in DemoWrapper); the real page is /deal/:id. The /demo path prefix
  // is the unambiguous, weakest-assumption signal — direct links work too.
  const isDemo = location.pathname.startsWith('/demo')
  const selectedOrgId = useSelectedOrgId()
  // updateDeal hits POST /deals/update so the user can change the deal
  // stage from the page header. Demo mode skips the network hit (no
  // backend deal to mutate). Closes #234.
  const { updateDeal: persistDealUpdate, deleteDeal } = useDealOperations(isDemo ? undefined : selectedOrgId)
  const [stageUpdating, setStageUpdating] = useState(false)
  // Header More-menu delete. Confirm-gated: opening the menu and clicking
  // Delete only ARMS the AlertDialog; the destructive /deals/delete call
  // fires solely from the dialog's explicit Delete button. Demo mode is
  // route-authoritative (isDemo) and never hits the real API.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingDeal, setDeletingDeal] = useState(false)
  const { demoDeals, demoActionItems, demoParticipants, getDemoContractById } = useDemoDealsDocuments()
  const { dealSummary: DEMO_DEAL_SUMMARY, keyDealTerms: KEY_DEAL_TERMS, exampleActivity: EXAMPLE_ACTIVITY } = useDealPageData()
  const { openDialog } = useUploadDocument()
  const id = propDealId || paramId

  // Fetch real deal in production mode
  const { data: apiDealData, isLoading: apiDealLoading, mutate: refetchDealData } = useSWR(
    !isDemo && id ? ['deal-page', id] : null,
    () => getDealApi({ dealId: id!, includeVersions: true }),
  )

  // Engine progress wiring: deals/get's response carries extra
  // `latestAnalysis` + `files` fields (loose-typed on the wire — not in
  // api-model). When latestAnalysis is present we mount the live
  // DealProgressTimeline; latestAnalysis.primaryContractId tells us
  // which contract to fetch for the rich envelope (dealFacts, dealScore,
  // eqCardsRaw, …) so the page can show real numbers instead of empty
  // demo stubs. The frontend cast at the boundary is the same Path A
  // pattern used elsewhere — keeps api-model stable.
  const dealExtras = apiDealData as unknown as {
    latestAnalysis?: {
      dealAnalysisId?: string;
      status?: string;
      primaryContractId?: string;
    };
    files?: Array<{
      fileId: string;
      fileName: string;
      contractId: string;
      fileType?: string;
      sizeBytes?: number;
      createdAt?: string;
    }>;
  } | undefined;
  const dealAnalysisId = dealExtras?.latestAnalysis?.dealAnalysisId ?? null;
  const primaryContractId = dealExtras?.latestAnalysis?.primaryContractId ?? null;
  const dealFiles = dealExtras?.files ?? [];

  // Phase 3 — intermediate-page projections (sourceType, threads,
  // participants, files-with-source). Pulls from the same /deals/get
  // response as `dealExtras` above; the hook just types the cast in one
  // place so the new sections consume a clean shape.
  const intermediate = useDealIntermediateData(apiDealData);

  // Fetch the linked contract's analysis envelope. /getContract carries
  // dealFacts, dealScore, eqCardsRaw, clauseSummaries, riskItems,
  // rightsExtraction, advanceSchedule — everything the DealPage needs
  // to render real numbers instead of the empty `useDealPageData`
  // stubs. Skipped in demo mode (mock data covers the demo path).
  const { data: apiContractData } = useSWR(
    !isDemo && primaryContractId ? ['deal-page-contract', primaryContractId] : null,
    () => getContractApi(primaryContractId!),
  );

  // Project the contract's engine envelope into the dealSummary shape
  // the page renders. Each field falls back to '—' when the engine
  // hasn't extracted it yet — matches the page's existing typography
  // for missing values rather than rendering 0 / empty string.
  const realDealSummary = useMemo(() => {
    if (isDemo) return null;
    const c = apiContractData as unknown as {
      dealFacts?: {
        totalAdvance?: { amount?: number; currency?: string };
        backendRoyaltyPercent?: number;
        effectiveDate?: string;
        deliverables?: string[];
        term?: { years?: number; description?: string };
      };
      rightsExtraction?: { territory?: string };
    } | undefined;
    if (!c) return null;
    const totalAdvance = c.dealFacts?.totalAdvance?.amount;
    const currency = c.dealFacts?.totalAdvance?.currency ?? 'USD';
    const formatMoney = (n?: number) =>
      typeof n === 'number'
        ? n >= 1_000_000
          ? `$${(n / 1_000_000).toFixed(1)}M`
          : n >= 1_000
            ? `$${(n / 1000).toFixed(0)}K`
            : `$${n.toLocaleString()}`
        : '—';
    const formatDate = (iso?: string) =>
      iso
        ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '—';
    return {
      dealValue: totalAdvance != null ? `${formatMoney(totalAdvance)} ${currency !== 'USD' ? currency : ''}`.trim() : '—',
      royaltySplit: typeof c.dealFacts?.backendRoyaltyPercent === 'number'
        ? `${c.dealFacts.backendRoyaltyPercent}%`
        : '—',
      startDate: formatDate(c.dealFacts?.effectiveDate),
      territory: c.rightsExtraction?.territory ?? '—',
      duration: c.dealFacts?.term?.description
        ?? (typeof c.dealFacts?.term?.years === 'number' ? `${c.dealFacts.term.years} years` : '—'),
      albumCount: c.dealFacts?.deliverables?.length ?? 0,
      // Fields the engine doesn't produce yet — keep blanks rather than
      // pretend to know.
      targetClose: '—',
      progressPercent: 0,
      totalDays: 0,
      advancePercent: 0,
      dealType: '',
      daysElapsed: 0,
      description: '',
    };
  }, [isDemo, apiContractData]);

  // Resolve which dealSummary to render: real engine data if we have
  // it, otherwise the existing demo / empty-stub fallback. Same shape
  // either way so render code below doesn't branch.
  const effectiveDealSummary = realDealSummary ?? DEMO_DEAL_SUMMARY;

  const deal: DemoDeal | undefined = useMemo(() => {
    // Demo mode: look up from demo data
    const demoDeal = demoDeals.find((d) => d.id === id)
    if (demoDeal) return demoDeal

    // Production mode: map API response to page shape. Pull `value`,
    // `documentCount`, and `coreDocument` from the same Path A engine
    // envelope + dealFiles list the body of the page reads from, so
    // the header strip ("Title · Stage · $X") shows the real total
    // advance and the Core Document tile shows the actual filename
    // instead of "—".
    if (!apiDealData) return undefined
    const { deal: apiDeal, versions } = apiDealData
    const versionList = Object.values(versions ?? {}) as DealVersion[]
    const latest = [...versionList].sort((a, b) => b.versionNumber - a.versionNumber)[0]
    const content = latest?.content as { title?: string; description?: string } | undefined
    const primaryFileName = dealFiles[dealFiles.length - 1]?.fileName ?? "—"

    // Derive the header badge from the REAL engine analysis status instead of
    // a hardcoded "In Progress". latestAnalysis.status is the loose-typed
    // engine string (none | pending | processing | complete | failed | …);
    // project it onto the DemoDeal status union the badge renders. A failed
    // analysis surfaces as "Pending" — the union has no "Failed" member, and
    // "Pending" maps to the warning badge variant (line ~362) which is the
    // correct attention-needed signal. Unknown/absent → "In Progress" default.
    const analysisStatus = (dealExtras?.latestAnalysis?.status ?? "").toLowerCase()
    const derivedStatus: DemoDeal["status"] =
      analysisStatus === "complete" || analysisStatus === "completed"
        ? "Completed"
        : analysisStatus === "failed" || analysisStatus === "error"
          ? "Pending"
          : "In Progress"

    return {
      id: apiDeal.dealId,
      name: content?.title || `Deal ${apiDeal.dealId.slice(0, 8)}`,
      type: latest?.stage || "drafting",
      status: derivedStatus,
      value: realDealSummary?.dealValue ?? "—",
      date: new Date(apiDeal.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      updatedAt: new Date(apiDeal.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      documentCount: dealFiles.length,
      coreDocument: primaryFileName,
      coreContractId: primaryContractId ?? undefined,
    }
  }, [demoDeals, apiDealData, id, dealFiles, realDealSummary, primaryContractId])

  const [actionItems, setActionItems] = useState(demoActionItems)
  const [newComment, setNewComment] = useState("")
  const [participants, setParticipants] = useState<DemoParticipant[]>(demoParticipants)

  const handleInviteParticipant = useCallback((data: InviteParticipantFormData) => {
    const newParticipant: DemoParticipant = {
      id: `participant-${Date.now()}`,
      name: data.name,
      email: data.email,
      role: data.role,
      permission: data.permission,
      status: "pending",
      invitedAt: new Date().toISOString(),
    }
    setParticipants((prev) => [newParticipant, ...prev])
  }, [])

  const toggleActionItem = useCallback((actionId: string) => {
    setActionItems(items =>
      items.map(item =>
        item.id === actionId ? { ...item, completed: !item.completed } : item
      )
    )
  }, [])

  const { pendingActions, completedActions } = useMemo(() => ({
    pendingActions: actionItems.filter(a => !a.completed),
    completedActions: actionItems.filter(a => a.completed),
  }), [actionItems])

  const handleUpload = useCallback(() => {
    openDialog(id)
  }, [openDialog, id])

  const confirmDeleteDeal = useCallback(async () => {
    // Demo mode: no backend deal to delete — surface Coming-Soon and bail
    // before any network call. Route-authoritative via isDemo.
    if (isDemo) {
      setDeleteDialogOpen(false)
      notifyFeature("Delete deal", true)
      return
    }
    if (!id) return
    setDeletingDeal(true)
    try {
      await deleteDeal(id)
      toast.success("Deal deleted")
      setDeleteDialogOpen(false)
      // Leave the now-deleted deal page — back to the pipeline.
      navigate(PATH_ROOT.main.dealPipeline)
    } catch (err) {
      console.error("Failed to delete deal:", err)
      toast.error("Could not delete deal")
    } finally {
      setDeletingDeal(false)
    }
  }, [isDemo, id, deleteDeal, navigate])

  if (!isDemo && apiDealLoading && !deal) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingBar />
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="p-8">
        <h1 className="text-foreground text-2xl font-bold">Deal Not Found</h1>
        <p className="text-muted-foreground mt-2">No deal found with ID: {id}</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
      </div>
    )
  }

  // getDemoContractById only resolves demo seed contracts — calling it with a
  // real deal's coreContractId returns undefined and would silently swallow the
  // "View linked contract" link on production deals. Restrict it to demo; the
  // real path links directly to the analysis route by coreContractId below.
  const coreContract = isDemo && deal.coreContractId ? getDemoContractById(deal.coreContractId) : undefined

  // Client-side stage inference (display-only). Email-ingest deals land on
  // the page with a raw stage of "drafting" because backend auto-advance
  // isn't wired yet (Parker's separate concern). Once the engine has
  // populated the deal summary (realDealSummary != null) for an
  // email-sourced deal, the deal has moved past drafting in practice — so we
  // SHOW "negotiation" in the header badge + stage Select. This is a
  // render-time mapping only: we never POST /deals/update from it, so the
  // server's stored stage is untouched and a real user-driven stage change
  // still persists normally. Demo deals keep their static `deal.type`.
  const displayStage =
    !isDemo && realDealSummary && intermediate.sourceType === 'email' && deal.type === DealStage.drafting
      ? DealStage.negotiation
      : deal.type

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="px-4 py-3 sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft className="size-4" aria-hidden="true" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-foreground text-lg font-semibold">{deal.name}</h1>
                <Badge variant={deal.status === "Completed" ? "success" : deal.status === "In Progress" ? "info" : "warning"}>
                  {deal.status}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                {/* Stage selector — persists via /deals/update. Demo
                    mode falls back to read-only text. #234. */}
                {isDemo || !id ? (
                  <span>{deal.type}</span>
                ) : (
                  <Select
                    value={displayStage}
                    disabled={stageUpdating}
                    onValueChange={async (next) => {
                      if (next === displayStage) return
                      setStageUpdating(true)
                      try {
                        await persistDealUpdate({
                          dealId: id,
                          newStage: next as DealStage,
                          changeReason: `Stage changed from ${displayStage} to ${next} via deal page`,
                        })
                      } catch (err) {
                        console.error('Failed to persist stage change:', err)
                      } finally {
                        setStageUpdating(false)
                      }
                    }}
                  >
                    <SelectTrigger className="h-7 w-auto min-w-32 px-2 text-sm" aria-label="Change deal stage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DealStage.drafting}>Drafting</SelectItem>
                      <SelectItem value={DealStage.negotiation}>Negotiation</SelectItem>
                      <SelectItem value={DealStage.signing}>Signing</SelectItem>
                      <SelectItem value={DealStage.delivery}>Delivery</SelectItem>
                      <SelectItem value={DealStage.completed}>Completed</SelectItem>
                      <SelectItem value={DealStage.cancelled}>Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <span>·</span>
                <span>{deal.value}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* BUG #238 — Share + More buttons used to be onClick-less,
                so clicks did literally nothing. Share now copies the
                current page URL to the clipboard (genuinely useful for
                handing a link to a teammate); More toasts a Coming Soon
                until the deal-actions menu is designed. */}
            <Button
              variant="ghost"
              size="sm"
              aria-label="Share deal"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  toast.success("Deal link copied to clipboard");
                } catch {
                  toast.error("Could not copy link");
                }
              }}
            >
              <Share2 className="size-4" aria-hidden="true" />
            </Button>
            {/* More-menu — real Delete replaces the old Coming-Soon stub.
                Delete only ARMS the confirm dialog (state below); the
                destructive deleteDeal call lives in confirmDeleteDeal,
                gated on the AlertDialog's explicit Delete button. */}
            <Menu>
              <MenuTrigger
                aria-label="More options"
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <MoreVertical className="size-4" aria-hidden="true" />
              </MenuTrigger>
              <MenuPopup align="end" sideOffset={4} className="min-w-[160px]">
                <MenuItem
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete deal
                </MenuItem>
              </MenuPopup>
            </Menu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="flex h-full">
          {/* Left Column */}
          <div className="flex-1 min-w-0 border-r border-border h-full overflow-y-auto">
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <div className="p-4">
                <TabsList>
                  <TabsTab value="overview">
                    <ArrowUpRight className="size-4" />
                    Overview
                  </TabsTab>
                  <TabsTab value="documents">
                    <FolderOpen className="size-4" />
                    Documents
                  </TabsTab>
                  {/* <TabsTab value="contracts">
                    <ScrollText className="size-4" />
                    Contracts
                  </TabsTab> */}
                </TabsList>
              </div>

              <TabsPanel value="overview" className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Phase 3 → Slice 1 cleanup: the channel-participants
                      strip, linked-threads panel, and cross-channel-docs
                      grid moved out of Overview. People moves to the
                      right-rail Participants panel; threads + docs live
                      under the Documents tab. Overview is now just the
                      Deal Overview + Key Deal Terms hydration of the
                      key contract — matches the demo aesthetic. */}

                  {/* Live engine narration — only renders for prod deals
                      that have BOTH an active analysis AND a contract
                      file attached. Phase 2 thread-only deals (categorizer
                      create_new without a PDF) get a dealAnalysisId at
                      open time but the engine never runs (nothing to
                      analyze), so the timeline shows "Waiting for engine"
                      indefinitely. Suppress until a contract is attached.
                      Demo deals don't get a dealAnalysisId at all. */}
                  {!isDemo && dealAnalysisId && intermediate.files.length > 0 && (
                    <DealProgressTimeline
                      dealAnalysisId={dealAnalysisId}
                      initialStatus={dealExtras?.latestAnalysis?.status}
                    />
                  )}

                  {/* Deal Overview */}
                  <WidgetCard>
                    <WidgetCard.Header>
                      <div className="flex items-center gap-2 pl-2">
                        <ArrowUpRight className="text-muted-foreground size-5" aria-hidden />
                        <h3 className="text-foreground font-semibold">Deal Overview</h3>
                      </div>
                    </WidgetCard.Header>
                    <WidgetCard.Content className="p-2">
                      <div className="flex items-start gap-3 mb-6">
                        <p className="text-foreground text-sm leading-relaxed">
                          {/* Compose narrative from real engine values. Each
                              fragment only renders when the underlying value
                              is meaningful — no "0% of your advance" filler
                              when payment tracking isn't extracted yet. */}
                          {effectiveDealSummary.albumCount > 0 && (
                            <>This deal covers {effectiveDealSummary.albumCount} {effectiveDealSummary.albumCount === 1 ? 'deliverable' : 'deliverables'}{' '}</>
                          )}
                          {effectiveDealSummary.royaltySplit && effectiveDealSummary.royaltySplit !== '—' && (
                            <>with a {effectiveDealSummary.royaltySplit} backend royalty{' '}</>
                          )}
                          {effectiveDealSummary.territory && effectiveDealSummary.territory !== '—' && effectiveDealSummary.territory !== 'Not extracted yet' && (
                            <>across {effectiveDealSummary.territory}{' '}</>
                          )}
                          {effectiveDealSummary.advancePercent > 0 && (
                            <>— you've received {effectiveDealSummary.advancePercent}% of your advance payment.</>
                          )}
                          {effectiveDealSummary.albumCount === 0 && (!effectiveDealSummary.royaltySplit || effectiveDealSummary.royaltySplit === '—') && (
                            intermediate.files.length > 0 ? (
                              <>Engine analysis in progress — values populate as the contract is parsed.</>
                            ) : (
                              <>This deal originated from an inbound thread. Attach a contract to run the engine and surface deal facts.</>
                            )
                          )}
                        </p>
                      </div>

                      <div className="mb-6 p-3 rounded-xl bg-muted/50">
                        <div className="flex items-start gap-2">
                          <ScrollText className="size-4 text-muted-foreground mt-0.5" aria-hidden="true" />
                          <div className="flex-1">
                            <p className="text-muted-foreground text-xs mb-1">Core Document</p>
                            {/* Slice 2 — production: prefer the explicit/auto-picked
                                key contract from the api-server. Demo: deal.coreDocument
                                is a static string, render it as before. When the deal
                                has 2+ candidate PDFs, surface a picker so the user
                                can override the auto-default. */}
                            {!isDemo && intermediate.keyContract ? (
                              <>
                                {intermediate.keyContractCandidateFileIds.length >= 2 ? (
                                  <select
                                    className="text-foreground text-sm font-semibold bg-transparent border-0 cursor-pointer focus:outline-none focus:ring-0 -ml-1 px-1 hover:bg-muted/50 rounded"
                                    value={intermediate.keyContract.fileId}
                                    onChange={async (e) => {
                                      const nextFileId = e.target.value
                                      try {
                                        const { setKeyContractApi } = await import("@/api/deal/SetKeyContractApi")
                                        await setKeyContractApi({ dealId: id!, fileId: nextFileId })
                                        await refetchDealData()
                                        toast.success("Key contract updated")
                                      } catch (err) {
                                        console.error("setKeyContract failed", err)
                                        toast.error("Could not update key contract")
                                      }
                                    }}
                                  >
                                    {intermediate.files
                                      .filter((f) => intermediate.keyContractCandidateFileIds.includes(f.fileId))
                                      .map((f) => (
                                        <option key={f.fileId} value={f.fileId}>{f.fileName}</option>
                                      ))}
                                  </select>
                                ) : (
                                  <p className="text-foreground text-sm font-semibold">{intermediate.keyContract.fileName}</p>
                                )}
                                {intermediate.keyContract.contractId && (
                                  <Link
                                    to={`${PATH_ROOT.main.analysis}/${intermediate.keyContract.contractId}`}
                                    className="mt-1 text-primary text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                                  >
                                    View Full Contract Analysis
                                  </Link>
                                )}
                                {intermediate.keyContract.isAutoPicked && intermediate.keyContractCandidateFileIds.length >= 2 && (
                                  <p className="text-muted-foreground text-xs mt-1">Auto-picked. Choose another above to change.</p>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-foreground text-sm font-semibold">{deal.coreDocument}</p>
                                {coreContract ? (
                                  <Link
                                    to={`${isDemo ? PATH_ROOT.demo.analysis : PATH_ROOT.main.analysis}/${coreContract.contractId}`}
                                    className="mt-1 text-primary text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                                  >
                                    View linked contract: {coreContract.name}
                                  </Link>
                                ) : (
                                  // Real deal with a contract id but no intermediate.keyContract
                                  // projection yet — link straight to the analysis route by id so
                                  // the contract is still reachable instead of dead-ending.
                                  !isDemo &&
                                  deal.coreContractId && (
                                    <Link
                                      to={`${PATH_ROOT.main.analysis}/${deal.coreContractId}`}
                                      className="mt-1 text-primary text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                                    >
                                      View full contract analysis
                                    </Link>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-3 rounded-xl bg-muted/50">
                          <p className="text-muted-foreground text-xs mb-1">Deal Value</p>
                          <p className="text-foreground text-2xl font-semibold tabular-nums">{effectiveDealSummary.dealValue}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-muted/50">
                          <p className="text-muted-foreground text-xs mb-1">Time Remaining</p>
                          <p className="text-foreground text-2xl font-semibold tabular-nums">
                            {effectiveDealSummary.progressPercent}%{" "}
                            <span className="text-muted-foreground text-sm font-normal">
                              of {effectiveDealSummary.totalDays} days
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-3 mb-6">
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <Calendar className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{effectiveDealSummary.startDate}</p>
                            <p className="text-muted-foreground text-[10px]">Start</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <TrendingUp className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{effectiveDealSummary.targetClose}</p>
                            <p className="text-muted-foreground text-[10px]">Target</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <Globe className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{effectiveDealSummary.territory}</p>
                            <p className="text-muted-foreground text-[10px]">Territory</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <Clock className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{effectiveDealSummary.duration}</p>
                            <p className="text-muted-foreground text-[10px]">Duration</p>
                          </div>
                        </div>
                      </div>
                    </WidgetCard.Content>
                  </WidgetCard>

                  {/* Key Deal Terms */}
                  <WidgetCard>
                    <WidgetCard.Header>
                      <div className="flex items-center gap-2 pl-2">
                        <DollarSign className="text-muted-foreground size-5" aria-hidden />
                        <h3 className="text-foreground font-semibold">Key Deal Terms</h3>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="text-xs"
                        disabled={!isDemo && !primaryContractId}
                        onClick={() => {
                          // primaryContractId is null until the engine
                          // produces a DealAnalysis record. In demo mode
                          // we already have a coreContract.contractId
                          // wired earlier in this file (line ~483) so
                          // prefer it; otherwise the prod-side
                          // primaryContractId from latestAnalysis is
                          // what unblocks the route.
                          const target = isDemo
                            ? coreContract?.contractId
                            : primaryContractId
                          if (!target) return
                          const root = isDemo ? PATH_ROOT.demo.analysis : PATH_ROOT.main.analysis
                          navigate(`${root}/${target}`)
                        }}
                      >
                        View Full Contract Analysis
                      </Button>
                    </WidgetCard.Header>
                    <WidgetCard.Content className="p-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {KEY_DEAL_TERMS.map((term) => (
                          <div
                            key={term.id}
                            className="flex flex-col gap-4 p-3 rounded-xl bg-muted/50 min-h-80"
                          >
                            <KeyTermChart termId={term.id} />
                            <div>
                              <term.icon className={cn(
                                "size-5 mb-2",
                                term.type === "risk" ? "text-red-400" : "text-muted-foreground"
                              )} />
                              <p className="text-muted-foreground text-xs">{term.title}</p>
                              <p className="text-lg font-semibold text-foreground">{term.value}</p>
                              <p className="text-muted-foreground/70 text-xs mt-1">{term.detail}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </WidgetCard.Content>
                  </WidgetCard>
                </div>
              </TabsPanel>

              <TabsPanel value="documents" className="flex-1 overflow-y-auto">
                <div className="p-6 h-full">
                  <DocumentsTable
                    onUpload={handleUpload}
                    // View → open that file's linked contract analysis, the same
                    // route the "View Full Contract Analysis" link uses. Each
                    // file carries its own contractId, so this generalizes
                    // per-document instead of hardcoding the primary contract.
                    // Returns undefined (View disabled) when the file has no
                    // linked contract yet.
                    onDocumentView={(fileId) => {
                      const file = intermediate.files.find((f) => f.fileId === fileId)
                      if (!file?.contractId) return undefined
                      return () => navigate(`${PATH_ROOT.main.analysis}/${file.contractId}`)
                    }}
                    // Download → presigned-URL fetch + browser save, the same
                    // path the Vault file detail panel uses.
                    onDocumentDownload={async (fileId, fileName) => {
                      const { downloadFile, triggerBrowserDownload } = await import("@/api/consolidator")
                      const result = await downloadFile(fileId, fileName)
                      if (result.ok) {
                        triggerBrowserDownload(result.blob, result.fileName)
                      } else {
                        toast.error("Download failed")
                      }
                    }}
                    documents={isDemo ? undefined : intermediate.files.map((f) => ({
                      id: f.fileId,
                      name: f.fileName,
                      type: (f.fileType ?? '').includes('pdf') ? 'PDF' : (f.fileType ?? 'File'),
                      // Real files don't have a per-document "review status"
                      // tracked in the deal-engine pipeline yet — surface as
                      // "draft" until contract-state extraction is wired.
                      status: 'draft' as const,
                      linkedAnalysis: !!primaryContractId,
                      uploadedAt: f.createdAt ?? '',
                      updatedAt: f.createdAt ?? '',
                      size: f.sizeBytes
                        ? f.sizeBytes >= 1_000_000
                          ? `${(f.sizeBytes / 1_000_000).toFixed(1)} MB`
                          : `${(f.sizeBytes / 1_000).toFixed(0)} KB`
                        : '—',
                      uploadedBy: f.sourceChannel === 'email' ? 'Email' : f.sourceChannel === 'imessage' ? 'iMessage' : '—',
                    }))}
                  />
                </div>
              </TabsPanel>

              <TabsPanel value="contracts" className="flex-1 overflow-y-auto">
                {EmptyContractsState}
              </TabsPanel>
            </Tabs>
          </div>

          {/* Resize Handle */}
          <DragHandle />

          {/* Right Column */}
          <div className="h-full overflow-y-auto" style={{ width: "var(--deal-sidebar-width, 380px)", minWidth: 280, maxWidth: 520 }}>
            <div className="p-5 space-y-5">
              {/* Action Items */}
              <WidgetCard>
                <WidgetCard.Header>
                  <div className="flex items-center gap-2 pl-2">
                    <Flag className="text-muted-foreground size-5" aria-hidden />
                    <h3 className="text-foreground font-semibold">Action Items</h3>
                    {isDemo && pendingActions.length > 0 && (
                      <Badge variant="warning" size="sm">{pendingActions.length}</Badge>
                    )}
                    {!isDemo && intermediate.actionItems.length > 0 && (
                      <Badge variant="warning" size="sm">{intermediate.actionItems.length}</Badge>
                    )}
                  </div>
                </WidgetCard.Header>
                <WidgetCard.Content>
                  {isDemo ? (
                    <motion.div className="space-y-1.5">
                      <AnimatePresence mode="popLayout">
                        {pendingActions.map((action) => (
                          <ActionItem key={action.id} action={action} onToggle={toggleActionItem} />
                        ))}
                      </AnimatePresence>

                      {completedActions.length > 0 && (
                        <div className={cn("pt-2 mt-2", completedActions.length > 1 && "border-t border-border/50")}>
                          <p className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1.5">Completed ({completedActions.length})</p>
                          {completedActions.map((action) => (
                            <ActionItem key={action.id} action={action} onToggle={toggleActionItem} isCompleted />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ) : intermediate.actionItems.length === 0 ? (
                    <p className="text-muted-foreground text-xs italic px-2 py-1">
                      No action items yet — Ari surfaces them as the thread evolves.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {intermediate.actionItems.map((item, idx) => (
                        <li key={item.sourceMessageId ?? `${idx}-${item.description.slice(0, 20)}`} className="flex items-start gap-2">
                          <span
                            className={cn(
                              "mt-1 inline-block size-1.5 rounded-full shrink-0",
                              item.priority === "urgent" && "bg-destructive",
                              item.priority === "high" && "bg-orange-500",
                              item.priority === "medium" && "bg-yellow-500",
                              item.priority === "low" && "bg-muted-foreground/40",
                            )}
                            aria-label={`priority ${item.priority}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-foreground text-xs leading-snug">{item.description}</p>
                            {(item.dueDate || item.owner) && (
                              <p className="text-muted-foreground text-[10px] mt-0.5">
                                {item.owner ? <span>{item.owner}</span> : null}
                                {item.owner && item.dueDate ? <span> · </span> : null}
                                {item.dueDate ? <span>due {item.dueDate}</span> : null}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </WidgetCard.Content>
              </WidgetCard>

              {/* Participants */}
              <WidgetCard>
                <WidgetCard.Header>
                  <div className="flex items-center gap-2 pl-2">
                    <h3 className="text-foreground font-semibold">Participants</h3>
                  </div>
                  <InviteParticipantDialog
                    dealName={deal.name}
                    onInvite={handleInviteParticipant}
                  />
                </WidgetCard.Header>
                <WidgetCard.Content>
                  {isDemo ? (
                    <motion.div layout className="space-y-2.5">
                      <AnimatePresence mode="popLayout">
                        {participants.map((participant) => (
                          <ParticipantItem key={participant.id} participant={participant} />
                        ))}
                      </AnimatePresence>
                    </motion.div>
                  ) : intermediate.participants.length === 0 ? (
                    <p className="text-muted-foreground text-xs italic px-2 py-1">
                      No counterparties identified yet.
                    </p>
                  ) : (
                    <ul className="space-y-2.5">
                      {intermediate.participants.map((p) => {
                        const displayName = p.displayName ?? p.identifier;
                        const role = p.inferredDealRole ?? p.role ?? null;
                        const tentative = (p.inferredRoleConfidence ?? 1) < 0.4;
                        return (
                          <li key={p.dealParticipantId} className="flex items-center gap-2">
                            <Avatar fallback={displayName.charAt(0).toUpperCase()} className="size-7 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-foreground text-xs font-medium truncate">{displayName}</p>
                              {role && (
                                <p className="text-muted-foreground text-[10px] truncate">
                                  {role}{tentative ? ' (tentative)' : ''}
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </WidgetCard.Content>
              </WidgetCard>

              {/* Recent Activity */}
              <WidgetCard>
                <WidgetCard.Header>
                  <div className="flex items-center gap-2 pl-2">
                    <Clock className="text-muted-foreground size-5" aria-hidden />
                    <h3 className="text-foreground font-semibold">Recent Activity</h3>
                  </div>
                </WidgetCard.Header>
                <WidgetCard.Content>
                  <div className="flex gap-2 mb-4">
                    <Avatar fallback="Y" className="size-6 shrink-0" />
                    <div className="flex-1">
                      <label htmlFor="deal-comment" className="sr-only">Add a comment</label>
                      <input
                        id="deal-comment"
                        name="comment"
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment…"
                        autoComplete="off"
                        className="w-full bg-muted/50 text-foreground placeholder:text-muted-foreground/50 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                    <Button size="sm" variant="ghost" disabled={!newComment.trim()} className="h-7 w-7 p-0" aria-label="Send comment">
                      <Send className="size-3.5" aria-hidden="true" />
                    </Button>
                  </div>

                  {isDemo ? (
                    <div className="space-y-3">
                      {EXAMPLE_ACTIVITY.map((activity, index) => (
                        <div key={activity.id} className="flex gap-2.5">
                          <div className="flex flex-col items-center">
                            <Avatar
                              src={activity.avatar}
                              fallback={activity.user.charAt(0)}
                              className="size-6 shrink-0"
                            />
                            {index < EXAMPLE_ACTIVITY.length - 1 && (
                              <div className="w-px flex-1 bg-border mt-1.5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pb-3">
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-foreground font-medium text-xs">{activity.user}</span>
                              <span className="text-muted-foreground text-[10px]">{activity.action}</span>
                              <span className="text-muted-foreground/50 text-[10px] ml-auto">{activity.timestamp}</span>
                            </div>
                            {activity.action === "commented" ? (
                              <div className="mt-1 p-2 rounded-md bg-muted/50 text-foreground/70 text-xs leading-relaxed">
                                {activity.content}
                              </div>
                            ) : (
                              <p className="text-muted-foreground text-xs mt-0.5">{activity.content}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : intermediate.activityEvents.length === 0 ? (
                    <p className="text-muted-foreground text-xs italic px-2 py-1">
                      No activity yet — Ari will fill in as the thread evolves.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {intermediate.activityEvents.map((evt, index) => {
                        const actor = evt.actor ?? '—';
                        const when = (() => {
                          try { return new Date(evt.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
                          catch { return ''; }
                        })();
                        return (
                          <div key={`${evt.timestamp}-${index}`} className="flex gap-2.5">
                            <div className="flex flex-col items-center">
                              <Avatar fallback={actor.charAt(0).toUpperCase()} className="size-6 shrink-0" />
                              {index < intermediate.activityEvents.length - 1 && (
                                <div className="w-px flex-1 bg-border mt-1.5" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0 pb-3">
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-foreground font-medium text-xs truncate">{actor}</span>
                                <span className="text-muted-foreground text-[10px] uppercase tracking-wide">{evt.eventType}</span>
                                <span className="text-muted-foreground/50 text-[10px] ml-auto shrink-0">{when}</span>
                              </div>
                              <p className="text-muted-foreground text-xs mt-0.5 leading-snug">{evt.summary}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </WidgetCard.Content>
              </WidgetCard>
            </div>
          </div>
        </div>
      </div>

      {/* Header More-menu delete confirmation. Destructive action ONLY
          fires from the explicit Delete button — Escape / backdrop /
          Cancel never delete. */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && !deletingDeal) setDeleteDialogOpen(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be easily undone. The deal will be removed from your
              pipeline. Any linked documents and threads stay in your inbox.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={deletingDeal}
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" disabled={deletingDeal} onClick={confirmDeleteDeal}>
              {deletingDeal ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default DealPage
